import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { listCasinos } from "@/data/casinos";
import { FAQ, buildFAQSchema } from "@/components/FAQ";

export function BonusHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const top = listCasinos({ swedishLicense: true })
    .sort(
      (a, b) =>
        Number(b.priority_brand) - Number(a.priority_brand) ||
        b.overall_rating - a.overall_rating,
    )
    .slice(0, 8);
  const title = isSv ? "Casino bonus 2026 — komplett guide för Sverige" : "Casino bonus 2026 — complete Sweden guide";
  const desc = isSv
    ? "Jämför casinobonusar i Sverige. Välkomstbonus, free spins, cashback och bonus utan omsättningskrav. Alla villkor förklarade."
    : "Compare casino bonuses in Sweden. Welcome bonuses, free spins, cashback and no-wager bonuses. All terms explained.";

  const bonusTypes = [
    {
      label_sv: "Välkomstbonus",
      label_en: "Welcome bonus",
      icon: "🎁",
      desc_sv: "Den största bonusen, ges en gång per spelare på svenska sajter.",
      desc_en: "The largest bonus, granted once per player on Swedish sites.",
    },
    {
      label_sv: "Free spins",
      label_en: "Free spins",
      icon: "🎰",
      desc_sv: "Gratis snurr på utvalda slots, ofta del av välkomsterbjudandet.",
      desc_en: "Free spins on selected slots, often part of the welcome offer.",
    },
    {
      label_sv: "Bonus utan insättning",
      label_en: "No-deposit bonus",
      icon: "💸",
      desc_sv: "En bonus du får utan att sätta in pengar — sällsynt på svensk licens.",
      desc_en: "A bonus you receive without depositing — rare under Swedish license.",
    },
    {
      label_sv: "Cashback",
      label_en: "Cashback",
      icon: "↺",
      desc_sv: "En procentuell återbetalning av nettoförlusten under en period.",
      desc_en: "A percentage refund of net losses over a period.",
    },
    {
      label_sv: "Reload",
      label_en: "Reload",
      icon: "🔁",
      desc_sv: "Återkommande bonus på efterföljande insättningar — ovanligt på svensk licens.",
      desc_en: "A recurring bonus on subsequent deposits — uncommon under Swedish license.",
    },
    {
      label_sv: "Utan omsättningskrav",
      label_en: "No wagering",
      icon: "⚡",
      desc_sv: "Bonus där vinsten kan tas ut direkt — kontrollera villkoren noggrant.",
      desc_en: "A bonus where winnings can be withdrawn immediately — check the terms carefully.",
    },
    {
      label_sv: "Oddsbonus",
      label_en: "Betting bonus",
      icon: "⚽",
      desc_sv: "Insatsboost eller risk-fri första insats hos sportsbooks.",
      desc_en: "Stake boost or risk-free first bet at sportsbooks.",
    },
    {
      label_sv: "Live casino-bonus",
      label_en: "Live casino bonus",
      icon: "🎲",
      desc_sv: "Bonus avsedd för bordsspel och livestreamade dealers.",
      desc_en: "A bonus aimed at table games and live-streamed dealers.",
    },
  ];

  const faq = [
    {
      question_sv: "Vad är skillnaden mellan svensk licens och utländsk licens när det gäller bonus?",
      question_en: "How do bonus rules differ between Swedish and foreign licenses?",
      answer_sv:
        "Svensk licens tillåter endast en välkomstbonus per spelare. Utländska sajter kan erbjuda återkommande bonusar och större insättningsmatchningar — men i utbyte gäller andra och ofta tuffare omsättningskrav.",
      answer_en:
        "A Swedish license allows only one welcome bonus per player. Foreign sites can offer recurring bonuses and bigger deposit matches — but the trade-off is different and often tougher wagering requirements.",
    },
    {
      question_sv: "Vad är ett rimligt omsättningskrav?",
      question_en: "What is a reasonable wagering requirement?",
      answer_sv:
        "30x bonus är ett vanligt riktvärde. Lägre är bättre, men kontrollera också spelbidrag, max insats per spin och tidsgräns. Lägg ihop alla villkoren — inte bara siffran.",
      answer_en:
        "30x bonus is a common benchmark. Lower is better, but also check game contribution, max bet per spin and the time limit. Look at the whole package — not just the number.",
    },
    {
      question_sv: "Är free spins-vinster skattefria?",
      question_en: "Are free spins winnings tax-free?",
      answer_sv:
        "Hos casinon med svensk licens är vinsterna skattefria för dig som spelare. Hos casinon utanför EES kan vinster vara skattepliktiga.",
      answer_en:
        "At Swedish-licensed casinos winnings are tax-free for you as a player. At casinos outside the EEA winnings may be taxable.",
    },
    {
      question_sv: "Kan jag förlora mer än bonusen?",
      question_en: "Can I lose more than the bonus?",
      answer_sv:
        "Eftersom du måste sätta in pengar för att aktivera de flesta bonusar är ja — du kan förlora din egen insättning innan bonusen är genomspelad. Spela inom din budget.",
      answer_en:
        "Because you usually have to deposit to activate a bonus, yes — you can lose your own deposit before the bonus is cleared. Play within your budget.",
    },
  ];

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} jsonLd={buildFAQSchema(faq, lang)} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Hur svenska casinobonusar fungerar" : "How Swedish casino bonuses work"}</h2>
        <p>
          {isSv
            ? "Sedan den nya svenska spellagstiftningen 2019 får varje licensierad operatör endast erbjuda en bonus per spelare. Det är en stor förändring jämfört med 2018 då bonusinflationen var en av branschens största kostnader. Idag är bonusens storlek mindre men villkoren är tydligare — och du kan inte luras av återkommande ”reload”-erbjudanden eller jakten på nya konton."
            : "Since Sweden's 2019 gambling reform every licensed operator may only offer one bonus per player. It is a big change from 2018 when bonus inflation was one of the industry's largest costs. Today's bonus is smaller but its terms are clearer — and you cannot be lured by repeated ”reload” offers or chasing new accounts."}
        </p>
        <h2>{isSv ? "Vad du ska kontrollera" : "What to check"}</h2>
        <p>
          {isSv
            ? "Innan du klickar ”ja” på en bonus, kontrollera fem saker: omsättningskravet (vanligen 25–40x), max insats per spin (typiskt 50 kr), tidsgränsen (oftast 7–30 dagar), spelbidrag (slots 100 %, bordsspel ofta 5–10 %) och eventuella exkluderade spel. Det är inte tråkig administration — det är skillnaden mellan att ta ut vinsten och att förlora bonusen."
            : "Before you click ”yes” on a bonus, check five things: the wagering requirement (typically 25–40x), max bet per spin (commonly 50 SEK), the time limit (usually 7–30 days), game contribution (slots 100%, table games often 5–10%) and any excluded games. It is not boring admin — it is the difference between cashing out and losing the bonus."}
        </p>
      </article>

      <section className="mt-10">
        <h2 className="text-2xl font-bold">{isSv ? "Bonustyper" : "Bonus types"}</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {bonusTypes.map((b) => (
            <div key={b.label_en} className="card p-5">
              <div className="text-2xl">{b.icon}</div>
              <h3 className="mt-2 font-semibold">{isSv ? b.label_sv : b.label_en}</h3>
              <p className="mt-1 text-sm text-ink-600">
                {isSv ? b.desc_sv : b.desc_en}
              </p>
            </div>
          ))}
        </div>
      </section>

      <h2 className="mt-12 text-2xl font-bold">
        {isSv ? "Casinon med svensk licens" : "Swedish-licensed casinos"}
      </h2>
      <div className="mt-4 grid gap-4">
        {top.map((c, i) => (
          <CasinoCard key={c.id} casino={c} rank={i + 1} source="bonus-hub" />
        ))}
      </div>

      <div className="mt-10">
        <FAQ items={faq} />
      </div>

      <InternalLinks context="bonus" />
    </div>
  );
}
